import {
    updateSharedState, compsInfo, createInfoToken, productBaseInfoToken, citySalesInfoToken, supplierInfoToken, getCityInfo, operateInfoToken,
} from './shareState';

export const handleSkuBaseSection = (vm) => {
    const data = vm.getVMPostData();

    data.sgProps = {
        ...vm.sgProps,
        extAttrs: vm.sgProps.extAttrs.map((item) => {
            // 根据attrValueType的值映射到新的值
            const valueTypeMapping = { 3: 1, 4: 2, default: 3 };
            const attrValueType = valueTypeMapping[item.valueType] || valueTypeMapping.default;
            return {
                attrId: item.attrId,
                attrName: item.attrName,
                attrValueType,
                need: item.required ? 1 : 2,
                sgExtAttrValues: item.selectedValues,
            };
        }),
    };
    // 储藏温度调至全国需求、不传宅配温段字段了
    data.storageTemperatures = null;
    data.edibleType = '';
    return data;
};

let baseModel; // 保证是单例的
class BaseModel {
    constructor() {
        this.vms = {}; // 维护注册下来的各个步骤对应的 VM 实例，通过 VM 实例来提取各个组件内的交互信息，进而提交后端保存。
        this.data = null; // 原始草稿数据
        this.models = { // 4部分参数的集合
            applyInfoSection: null, // 提报信息
            skuBaseSection: null, // 基础信息
            citySkuManageSection: null, // 资质信息
            qualificationSection: null, // 供应商资质
            contentSection: null, // 内容信息
            applyId: 0, // 第一次保存会返回applyId,之后的保存需要带着
            completeSectionStage: 1, // 当前编辑的第几步 传1,2,3,4,5
            complete: false, // 当前是否完成(点了下一步)
            rejectNodes: null, // 被驳回重新发起记录的节点，前端不关心，后端使用
            version: null,
            canChangeMisId: false, // 是否可以编辑msid
            ocrDescList: [], // ocr说明信息
            presetSkuId: 0, // 预设skuId
            preSkuId: 0,
        };
    }

    registerVM(vm, token) {
        this.vms[token] = vm;
    }

    getVM(token) {
        return this.vms[token];
    }

    // Note: BaseModel --> VM Model 的 adapter
    get initCreateInfo() {
        if (!(this.data && this.data.applyInfoSection)) return null;
        /*
        *  skuCityIds；已有sku绑定过的城市，
        *  newSalesCityList；通过 getCityInfo 可以根据 id 获取城市信息
        *  applyReason
        *  applyDetail
        *  allSelectedCityIds 当前所有被选中的 city，包括 原来 sku 绑定的 以及 用户新勾选的
        */

        const data = this.data.applyInfoSection;
        // data.citySku 返回的是全部城市，包括已有sku城市 + 新增sku城市。
        // exist = true，表示已有sku城市，false表示新增sku城市
        const cityIds = data.citySku ? data.citySku.filter((item) => item.exist).map((item) => item.cityId) : [];
        // 保存已有sku城市ids到shared state中
        updateSharedState('existedCityIds', cityIds);
        // 本次新增的城市
        const newSalesCityList = data.citySku ? data.citySku.filter((item) => !item.exist).map((info) => ({
            cityName: getCityInfo(info.cityId).label,
            ...info,
            planOnSaleTime: info.planOnSaleTime <= 0 ? Date.now() : info.planOnSaleTime,
        })) : [];
        // 全量被勾选的城市，包括已有和新增勾选
        const allSelectedCityIds = (data.citySku || []).map((item) => item.cityId) || [];
        // 单位处理成字符串
        data.unitId = data.unitId > 0 ? String(data.unitId) : '';

        return {
            cityIds,
            allSelectedCityIds,
            newSalesCityList,
            form: data,
        };
    }

    get initProductBaseInfo() {
        if (!(this.data && this.data.skuBaseSection)) return null;
        // 初始化预设skuId
        this.models.presetSkuId = this.data.presetSkuId;

        const { skuBaseSection } = this.data;
        return skuBaseSection;
    }

    get initCitySalesInfo() {
        if (!(this.data && this.data.citySkuManageSection && this.data.citySkuManageSection.citySkuManageInfo)) return null;
        // 这里返回的都是新增的城市
        const { citySkuManageInfo } = this.data.citySkuManageSection;
        (citySkuManageInfo || []).forEach((cityInfo) => {
            // 字符串类型兼容
            cityInfo.sellUnitId = String(cityInfo.sellUnitId);
            // 防止补货步长不填写情况下，被后端重置为0
            cityInfo.replenishStep = cityInfo.replenishStep > 0 ? cityInfo.replenishStep : null;
            // 售卖类型处理为Null
            cityInfo.sellPurposeType = cityInfo.sellPurposeType > 0 ? cityInfo.sellPurposeType : null;
        });
        return citySkuManageInfo;
    }

    get initSupplierInfo() {
        if (!(this.data && this.data.qualificationSection && this.data.qualificationSection.qualificationList)) return null;
        this.data.qualificationSection.qualificationList.forEach((section) => {
            section.items.forEach((item) => {
                if (!item.pictures) item.pictures = [];
            });
        });
        return this.data.qualificationSection.qualificationList;
    }

    get initOperateInfo() {
        if (!(this.data && this.data.contentSection)) return null;
        return this.data.contentSection;
    }

    // 当触发 保存/提交 的时候，通过 baseModel 的 save 完成：a.从对应 vm 中提取信息  b.做需要接口参数的转换 adater c.触发保存接口
    async saveModel(token, isCompleted) {
        const stepIndex = compsInfo[token].index;
        const vm = this.vms[token];
        this.models.completeSectionStage = stepIndex;
        this.models.complete = isCompleted;
        switch (token) {
            case createInfoToken:
                const applyInfoSection = (this.data && this.data.applyInfoSection) || {};
                await this.saveCreateInfo(vm, applyInfoSection, stepIndex, isCompleted);
                break;
            case productBaseInfoToken:
                const skuBaseSection = (this.data && this.data.skuBaseSection) || {};
                await this.saveProductBaseInfo(vm, skuBaseSection, stepIndex, isCompleted);
                break;
            case citySalesInfoToken:
                const citySkuManageSection = (this.data && this.data.citySkuManageSection) || {};
                await this.saveCitySalesInfo(vm, citySkuManageSection, stepIndex, isCompleted);
                break;
            case supplierInfoToken:
                const qualificationSection = (this.data && this.data.qualificationSection) || {};
                await this.saveSupplierInfo(vm, qualificationSection, stepIndex, isCompleted);
                break;
            case operateInfoToken:
                const contentSection = (this.data && this.data.contentSection) || {};
                await this.saveOperateInfo(vm, contentSection, stepIndex, isCompleted);
                break;

            default:
                throw new Error(`未找到匹配的 token = ${token}`);
        }
    }

    // (vm, draftData, stepIndex, isCompleted)
    async saveCreateInfo(vm) {
        // 往 draftData 中回写对应的 get adapter 的内容，返回要 post 的数据
        /*
            *  skuCityIds；
            *  newSalesCityList；通过 getCityInfo 可以根据 id 获取城市信息
            *  applyReason
            *  applyDetail
            */
        this.models.applyInfoSection = vm.getVMPostData();

        // 第一次保存草稿，将applyId保存后续使用
        const { applyId, version } = await vm.$service.saveNewSellDraft(this.models);
        this.models.applyId = applyId;
        this.models.version = version;
        // 将管理类目id存入share state
        updateSharedState('categoryId', this.models.applyInfoSection.categoryId);
    }

    async saveProductBaseInfo(vm) {
        // 处理配方单结构，多包一层recipeTemplate
        // data.recipes = data.recipes.map((item) => ({
        //     cityIds: item.cityIds,
        //     recipeTemplate: window._.pick(item, ['recipeTemplateId', 'skuBaseId', 'recipeTemplateName', 'recipeTemplateType', 'recipeType', 'stockFlag', 'poiList', 'recipeIngredientList', 'overflowLossRate']),
        // }));
        // 储藏温度调至全国需求、不传宅配温段字段了
        // 宅配温段字段隐藏，传递参数和储藏温度保持一致
        // if (data.storageTemperatures && data.storageTemperatures.length) {
        //     data.deliveryTemperatures = data.storageTemperatures.map((item) => ({
        //         cityIds: item.cityIds,
        //         deliveryTemperatureLevel: item.storageTemperatureLevel,
        //         existedIds: item.existedIds,
        //     }));
        // }

        this.models.skuBaseSection = handleSkuBaseSection(vm);
        const { version } = await vm.$service.saveNewSellDraft(this.models);
        this.models.version = version;
    }

    async saveCitySalesInfo(vm) {
        // 适配接口格式 citySkuManageSection: { citySkuManageInfo: []<cityInfo> }
        const cityInfoMap = vm.getVMPostData();
        if (!this.models.citySkuManageSection) this.models.citySkuManageSection = {};
        this.models.citySkuManageSection.citySkuManageInfo = Object.keys(cityInfoMap).map((cityId) => cityInfoMap[cityId]);
        const { version } = await vm.$service.saveNewSellDraft(this.models);
        this.models.version = version;
    }

    async saveSupplierInfo(vm) {
        if (!this.models.qualificationSection) this.models.qualificationSection = {};
        this.models.qualificationSection.qualificationList = vm.getVMPostData();
        const { version } = await vm.$service.saveNewSellDraft(this.models);
        this.models.version = version;
    }

    async saveOperateInfo(vm) {
        const data = vm.getVMPostData();
        // 临时处理图片尺寸，等图片尺寸全部调整完，可以下掉
        await vm.picSizeRewrite(data).catch(() => {});

        this.models.contentSection = data;
        const { version } = await vm.$service.saveNewSellDraft(this.models);
        this.models.version = version;
    }


    async submitModel(token) {
        const vm = this.vms[token];
        await vm.$service.submitNewSell(this.models);
    }

    // 从接口中获取 草稿详情
    setData(data) {
        if (data.ocrDescList && data.ocrDescList.length) this.models.ocrDescList = data.ocrDescList;
        this.data = data;
        this.models.version = data.version;
    }

    get newSalesCityList() {
        return this.vms[createInfoToken] ? this.vms[createInfoToken].form.cityIds : [];
    }

    // 通过管理类目id获取配置数据
    async getCategoryConfigByFlowId(vm) {
        if (!this.models.applyId) return null;
        const data = await vm.$service.queryCategoryConfigByApplyId({ applyId: this.models.applyId }).catch(() => null);
        if (!data) return null;
        updateSharedState('categoryConfig', data);
        // data.flowSpec.canChangeEditor = true;
        return data;
    }
}

export function getBaseModel() {
    if (baseModel) return baseModel;
    baseModel = new BaseModel();
    return baseModel;
}

export default {};
